import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Tooltip, Button, CircularProgress, Alert,
  Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tabs, Tab, Divider, Stack
} from '@mui/material';
import {
  CheckCircle, Cancel, Refresh, Visibility, CalendarMonth,
  Person, Email, Phone, DirectionsCar, AccessTime
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem('token');

const STATUS_CHIP = {
  pendente:   { label: 'Pendente',   color: 'warning' },
  confirmada: { label: 'Confirmada', color: 'success' },
  cancelada:  { label: 'Cancelada',  color: 'error' }
};

export default function PreReservas() {
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [refreshKey, setRefreshKey] = useState(0);

  const [confirmandoId, setConfirmandoId] = useState(null);
  const [cancelandoId, setCanceladoId] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [dialogCancel, setDialogCancel] = useState(null); // pré-reserva a cancelar
  const [detalhes, setDetalhes] = useState(null);

  const carregar = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    /* eslint-disable react-hooks/set-state-in-effect */
    setLoading(true);
    setErro('');
    /* eslint-enable react-hooks/set-state-in-effect */
    axios
      .get(`${API_URL}/pre-reservas`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(res => { if (!cancelled) setReservas(res.data.pre_reservas || []); })
      .catch(() => { if (!cancelled) setErro('Erro ao carregar pré-reservas'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  async function confirmar(pr) {
    setConfirmandoId(pr.id);
    try {
      await axios.post(`${API_URL}/pre-reservas/${pr.id}/confirmar`, {}, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao confirmar');
    } finally {
      setConfirmandoId(null);
    }
  }

  async function cancelar() {
    if (!dialogCancel) return;
    setCanceladoId(dialogCancel.id);
    try {
      await axios.post(`${API_URL}/pre-reservas/${dialogCancel.id}/cancelar`, { motivo }, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setDialogCancel(null);
      setMotivo('');
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao cancelar');
    } finally {
      setCanceladoId(null);
    }
  }

  const filtradas = filtroStatus === 'todos'
    ? reservas
    : reservas.filter(r => r.status === filtroStatus);

  const contar = (s) => reservas.filter(r => r.status === s).length;

  const formatarData = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
  const formatarDH = (d) => d ? new Date(d).toLocaleString('pt-BR') : '-';
  const calcDias = (r, d) => {
    if (!r || !d) return '-';
    return Math.ceil((new Date(d) - new Date(r)) / (1000 * 60 * 60 * 24)) + ' dias';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Pré-Reservas</Typography>
          <Typography variant="body2" color="text.secondary">Solicitações recebidas pelo widget do site</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={carregar}
          disabled={loading}
          sx={{ borderColor: '#ED3237', color: '#ED3237' }}
        >
          Atualizar
        </Button>
      </Box>

      {/* Cards de resumo */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: reservas.length, color: '#1976d2' },
          { label: 'Pendentes', value: contar('pendente'), color: '#ED3237' },
          { label: 'Confirmadas', value: contar('confirmada'), color: '#2e7d32' },
          { label: 'Canceladas', value: contar('cancelada'), color: '#d32f2f' }
        ].map(c => (
          <Card key={c.label} sx={{ flex: '1 1 140px', border: `2px solid ${c.color}20`, minWidth: 120 }}>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ color: c.color, fontWeight: 700 }}>{c.value}</Typography>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

      {/* Filtro por status */}
      <Tabs
        value={filtroStatus}
        onChange={(_, v) => setFiltroStatus(v)}
        sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}
      >
        <Tab label={`Todos (${reservas.length})`} value="todos" />
        <Tab label={`Pendentes (${contar('pendente')})`} value="pendente" />
        <Tab label={`Confirmadas (${contar('confirmada')})`} value="confirmada" />
        <Tab label={`Canceladas (${contar('cancelada')})`} value="cancelada" />
      </Tabs>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: '#ED3237' }} /></Box>
      ) : filtradas.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CalendarMonth sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography color="text.secondary">Nenhuma pré-reserva encontrada</Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell>#</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Veículo</TableCell>
                <TableCell>Período</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Solicitado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtradas.map((pr) => (
                <TableRow key={pr.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#ED3237' }}>#{pr.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{pr.nome_cliente}</Typography>
                    <Typography variant="caption" color="text.secondary">{pr.email_cliente}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{pr.modelo || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{pr.placa || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatarData(pr.data_retirada)} → {formatarData(pr.data_devolucao)}</Typography>
                    <Typography variant="caption" color="text.secondary">{calcDias(pr.data_retirada, pr.data_devolucao)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_CHIP[pr.status]?.label || pr.status}
                      color={STATUS_CHIP[pr.status]?.color || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{formatarDH(pr.criado_em)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Ver detalhes">
                        <IconButton size="small" onClick={() => setDetalhes(pr)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {pr.status === 'pendente' && (
                        <>
                          <Tooltip title="Confirmar reserva (envia e-mail ao cliente)">
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => confirmar(pr)}
                                disabled={confirmandoId === pr.id}
                              >
                                {confirmandoId === pr.id
                                  ? <CircularProgress size={16} />
                                  : <CheckCircle fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDialogCancel(pr)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog: Detalhes */}
      <Dialog open={!!detalhes} onClose={() => setDetalhes(null)} maxWidth="sm" fullWidth>
        {detalhes && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Pré-Reserva #{detalhes.id}</span>
              <Chip
                label={STATUS_CHIP[detalhes.status]?.label}
                color={STATUS_CHIP[detalhes.status]?.color}
                size="small"
              />
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Cliente</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="body2">{detalhes.nome_cliente}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{detalhes.email_cliente}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{detalhes.telefone_cliente}</Typography>
                    </Box>
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="overline" color="text.secondary">Veículo</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <DirectionsCar fontSize="small" color="action" />
                    <Typography variant="body2">{detalhes.modelo} — {detalhes.placa}</Typography>
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="overline" color="text.secondary">Período</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <CalendarMonth fontSize="small" color="action" />
                    <Typography variant="body2">
                      {formatarData(detalhes.data_retirada)} → {formatarData(detalhes.data_devolucao)} ({calcDias(detalhes.data_retirada, detalhes.data_devolucao)})
                    </Typography>
                  </Box>
                  {detalhes.local_retirada && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 3 }}>
                      📍 {detalhes.local_retirada}
                    </Typography>
                  )}
                </Box>
                {detalhes.observacoes && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">Observações do Cliente</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{detalhes.observacoes}</Typography>
                    </Box>
                  </>
                )}
                {detalhes.observacoes_cancelamento && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="error">Motivo do Cancelamento</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: 'error.main' }}>{detalhes.observacoes_cancelamento}</Typography>
                    </Box>
                  </>
                )}
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Solicitado em {formatarDH(detalhes.criado_em)}</Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ gap: 1, p: 2 }}>
              {detalhes.status === 'pendente' && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => { confirmar(detalhes); setDetalhes(null); }}
                  >
                    Confirmar
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => { setDialogCancel(detalhes); setDetalhes(null); }}
                  >
                    Cancelar
                  </Button>
                </>
              )}
              <Button onClick={() => setDetalhes(null)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog: Cancelar com motivo */}
      <Dialog open={!!dialogCancel} onClose={() => setDialogCancel(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar Pré-Reserva #{dialogCancel?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe o motivo do cancelamento (opcional). O cliente será notificado.
          </Typography>
          <TextField
            fullWidth
            label="Motivo do cancelamento"
            multiline
            rows={3}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex: Veículo não disponível para esse período"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogCancel(null); setMotivo(''); }}>Voltar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={cancelar}
            disabled={!!cancelandoId}
            startIcon={cancelandoId ? <CircularProgress size={16} color="inherit" /> : <Cancel />}
          >
            Cancelar Reserva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
